import { Link, Outlet, NavLink, useNavigate, Navigate, useLocation } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { LogOut, Package, LayoutGrid, ShoppingBag, LayoutDashboard, FileText, FilePlus, Share2, Menu, X, Mail } from "lucide-react";
import { useEffect, useState } from "react";

import { TRANSPARENT_LOGO } from "@/lib/brand";
const LOGO = TRANSPARENT_LOGO;

const LINKS = [
  { to: "/admin", label: "Dashboard", icon: LayoutDashboard, end: true },
  { to: "/admin/products", label: "Products", icon: Package },
  { to: "/admin/collections", label: "Collections", icon: LayoutGrid },
  { to: "/admin/orders", label: "Orders", icon: ShoppingBag },
  { to: "/admin/content", label: "Site Content", icon: FileText },
  { to: "/admin/pages", label: "Pages", icon: FilePlus },
  { to: "/admin/social", label: "Social & Marketing", icon: Share2 },
  { to: "/admin/subscribers", label: "Subscribers", icon: Mail },
];

export default function AdminLayout() {
  const { email, loading, logout } = useAuth();
  const nav = useNavigate();
  const { pathname } = useLocation();
  const [drawer, setDrawer] = useState(false);
  useEffect(() => { setDrawer(false); }, [pathname]);

  if (loading) return <div className="min-h-screen flex items-center justify-center text-brass font-caption">Loading…</div>;
  if (!email) return <Navigate to="/admin/login" replace />;

  const current = LINKS.find((l) => l.end ? pathname === l.to : pathname.startsWith(l.to));

  const SidebarBody = ({ onClick }) => (
    <>
      <div className="p-6 border-b border-brass-soft flex items-center gap-3">
        <img src={LOGO} className="h-10" alt="" />
        <div>
          <div className="font-display text-gold text-lg leading-tight">Ledger</div>
          <div className="text-xs text-parchment/60 break-all">{email}</div>
        </div>
      </div>
      <nav className="p-4 space-y-1">
        {LINKS.map((l) => (
          <NavLink key={l.to} to={l.to} end={l.end} onClick={onClick}
            data-testid={`admin-nav-${l.label.toLowerCase().replace(/[ &]/g, '-')}`}
            className={({ isActive }) => `flex items-center gap-3 px-3 py-2.5 text-sm border-l-2 ${isActive ? "border-gold text-gold bg-[rgba(212,175,55,0.06)]" : "border-transparent text-parchment/80 hover:text-gold"}`}>
            <l.icon size={16} /> {l.label}
          </NavLink>
        ))}
        <button data-testid="admin-logout" onClick={() => { logout(); nav("/admin/login"); }}
          className="flex items-center gap-3 px-3 py-2 text-sm text-parchment/60 hover:text-oxblood w-full mt-6"><LogOut size={16} /> Sign Out</button>
        <Link to="/" target="_blank" rel="noopener noreferrer" onClick={onClick}
          data-testid="admin-view-storefront"
          className="block px-3 py-2 text-xs text-brass font-caption mt-4">← View Storefront</Link>
      </nav>
    </>
  );

  return (
    <div className="min-h-screen flex flex-col lg:flex-row bg-forest">
      {/* Desktop sidebar */}
      <aside className="hidden lg:block lg:w-64 lg:border-r border-brass-soft bg-[color:var(--forest-2)] flex-shrink-0">
        <SidebarBody />
      </aside>

      {/* Mobile top bar */}
      <div className="lg:hidden sticky top-0 z-30 border-b border-brass-soft bg-[color:var(--forest-2)] px-4 py-3 flex items-center gap-3">
        <button aria-label="Open menu" data-testid="admin-mobile-menu-btn"
          onClick={() => setDrawer(true)} className="text-gold p-1.5 -ml-1"><Menu size={22} /></button>
        <img src={LOGO} className="h-8" alt="" />
        <div className="font-caption text-[10px] tracking-wider text-brass flex-1 truncate">
          {current?.label || "Ledger"}
        </div>
      </div>

      {/* Mobile drawer */}
      {drawer && (
        <div className="lg:hidden fixed inset-0 z-50 bg-[rgba(9,18,13,0.6)]" onClick={() => setDrawer(false)}>
          <aside className="w-72 max-w-[85vw] h-full bg-[color:var(--forest-2)] border-r border-brass-soft overflow-y-auto"
            onClick={(e) => e.stopPropagation()} data-testid="admin-mobile-drawer">
            <div className="flex justify-end p-2">
              <button data-testid="admin-mobile-drawer-close" onClick={() => setDrawer(false)} className="text-gold p-2"><X size={22} /></button>
            </div>
            <SidebarBody onClick={() => setDrawer(false)} />
          </aside>
        </div>
      )}

      <main className="flex-1 p-4 sm:p-6 lg:p-10 min-w-0">
        <Outlet />
      </main>
    </div>
  );
}
